import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { usePaymentMethod, useUser } from '@mopla/business-logic';
import { useLogger } from '@mopla/logger';
import { MplButton } from '@mopla/ui';
import { getCountdownTimer$ } from '@mopla/utils';

import { usePayByBankSetupStore } from '../../stores/usePayByBankSetupStore';
import {
	createPayByBankSetupPolling$,
	fetchPaymentMethodSetupStatus,
	PAY_BY_BANK_COUNTDOWN_SECONDS,
	PAY_BY_BANK_GRACE_MS,
} from '../../utils/payByBankSetupPolling';
import { usePaymentPortalControls } from '../../utils/usePaymentPortalControls';
import { PaymentErrorModal, PaymentLoadingOverlay } from '../common';

import { Countdown, PendingText } from './PayByBankSetupWaiting.styles';

const formatCountdown = (totalSeconds: number) => {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/** Full-screen waiting state after returning from the bank (Pay by Bank setup). */
export const PayByBankSetupWaiting: FC = () => {
	const { t } = useTranslation('payment');
	const logger = useLogger('payment');
	const { syncPaymentMethod } = usePaymentMethod();
	const { openPaymentPortal } = usePaymentPortalControls();
	const { userData } = useUser();
	const { phase, startedAt, storedUserId, resumeWaiting, markFailed, reset } =
		usePayByBankSetupStore(
			useShallow((state) => ({
				phase: state.phase,
				startedAt: state.startedAt,
				storedUserId: state.userId,
				resumeWaiting: state.resumeWaiting,
				markFailed: state.markFailed,
				reset: state.reset,
			}))
		);

	/** Keep the polling effect keyed on the waiting phase only, so re-renders never restart it. */
	const latest = useRef({
		syncPaymentMethod,
		logger,
		resumeWaiting,
		markFailed,
		reset,
	});
	latest.current = {
		syncPaymentMethod,
		logger,
		resumeWaiting,
		markFailed,
		reset,
	};

	const [remainingSeconds, setRemainingSeconds] = useState(
		PAY_BY_BANK_COUNTDOWN_SECONDS
	);

	/**
	 * After a reload/restart a stored marker is resumed only for the user who started it; the polling below
	 * (not a separate check) asks the backend. Needs a logged-in user (this component is mounted outside PrivateRoute).
	 */
	useEffect(() => {
		if (phase !== 'resuming' || !userData) return;
		if (userData.userId === storedUserId) {
			latest.current.resumeWaiting();
		} else {
			latest.current.reset();
		}
	}, [phase, userData, storedUserId]);

	useEffect(() => {
		if (phase !== 'waiting' || startedAt === null) return;
		const subscription = getCountdownTimer$(
			new Date(startedAt),
			PAY_BY_BANK_COUNTDOWN_SECONDS
		).subscribe((duration) => setRemainingSeconds(duration.asSeconds()));
		return () => subscription.unsubscribe();
	}, [phase, startedAt]);

	useEffect(() => {
		if (phase !== 'waiting' || startedAt === null) return;
		const { logger: log } = latest.current;
		const remainingMs =
			startedAt +
			PAY_BY_BANK_COUNTDOWN_SECONDS * 1000 +
			PAY_BY_BANK_GRACE_MS -
			Date.now();
		/** An already expired window (resumed late) still gives the backend one grace period to answer. */
		const deadlineMs = remainingMs > 0 ? remainingMs : PAY_BY_BANK_GRACE_MS;
		log.info('[PayByBankSetupWaiting]: start polling setup status');

		let cancelled = false;
		const subscription = createPayByBankSetupPolling$(
			fetchPaymentMethodSetupStatus,
			{
				deadlineMs,
			}
		).subscribe(async (result) => {
			const current = latest.current;
			current.logger.info('[PayByBankSetupWaiting]: setup result', { result });
			if (result === 'FAILED') {
				current.markFailed();
				return;
			}
			try {
				await current.syncPaymentMethod();
			} catch (error) {
				current.logger.error(
					'[PayByBankSetupWaiting]: syncPaymentMethod failed',
					error
				);
			}
			if (!cancelled) current.reset();
		});

		return () => {
			cancelled = true;
			subscription.unsubscribe();
		};
	}, [phase, startedAt]);

	if (phase === 'waiting') {
		return (
			<PaymentLoadingOverlay
				testId="PayByBankSetupWaiting-Overlay"
				fullScreen
				text={t('pay_by_bank_setup.pending_title')}
			>
				<Countdown data-testid="PayByBankSetupWaiting-Countdown" variant="h2">
					{formatCountdown(remainingSeconds)}
				</Countdown>
				<PendingText data-testid="PayByBankSetupWaiting-PendingText">
					{t('pay_by_bank_setup.pending_text')}
				</PendingText>
			</PaymentLoadingOverlay>
		);
	}

	if (phase === 'failed') {
		return (
			<PaymentErrorModal
				testId="PayByBankSetupWaiting-ErrorModal"
				title={t('pay_by_bank_setup.failed_title')}
				description={t('pay_by_bank_setup.failed_text')}
				onClose={reset}
				actions={
					<MplButton
						testId="PayByBankSetupWaiting-ErrorModal-ChooseAnotherButton"
						fullWidth
						onClick={() => {
							reset();
							openPaymentPortal();
						}}
					>
						{t('pay_by_bank_setup.choose_another')}
					</MplButton>
				}
			/>
		);
	}

	return null;
};
